import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { metricsApi } from "@/api/metrics";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PeriodNav } from "@/components/PeriodNav";
import { type Period, calendarRangeFor, fmt as fmtCanonical, num as numCanonical, todayIso } from "@/lib/format";

// Локальні shim'и: на цій сторінці NULL трактується як 0 (не «—»).
const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number, digits = 0): string => fmtCanonical(n, digits);

export function FinancePage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>("month");
  const [end, setEnd] = useState(todayIso());

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });

  const totals = useMemo(() => {
    const rows = daily.data ?? [];
    let revenue = 0;
    let cost = 0;
    let profit = 0;
    let imbalance = 0;
    let heat = 0;
    for (const r of rows) {
      revenue += num(r.fact_revenue_uah);
      profit += num(r.fact_profit_uah);
      imbalance += num(r.imbalance_payment_uah);
      heat += num(r.fact_heat_revenue_uah);
    }
    cost = revenue - profit;
    return { revenue, cost, profit, imbalance, heat };
  }, [daily.data]);

  const chartData = useMemo(() => {
    return (daily.data ?? []).map((r) => ({
      day: r.day.slice(5), // MM-DD
      revenue: num(r.fact_revenue_uah),
      profit: num(r.fact_profit_uah),
      plan_profit: num(r.plan_profit_uah),
    }));
  }, [daily.data]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("finance.title")}</CardTitle>
          <CardDescription>{t("finance.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label={t("finance.revenue")} value={fmt(totals.revenue)} />
        <Kpi label={t("finance.cost")} value={fmt(totals.cost)} />
        <Kpi
          label={t("finance.profit")}
          value={fmt(totals.profit)}
          tone={totals.profit >= 0 ? "positive" : "negative"}
        />
        <Kpi label={t("finance.imbalance")} value={fmt(totals.imbalance)} />
        <Kpi label={t("finance.heat_revenue")} value={fmt(totals.heat)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("finance.chart_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {daily.isLoading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Legend />
                  <Bar dataKey="plan_profit" name={t("finance.plan_profit")} fill="#94a3b8" />
                  <Bar dataKey="profit" name={t("finance.fact_profit")} fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div
          className={
            "text-xl font-mono font-semibold " +
            (tone === "negative" ? "text-red-700" : tone === "positive" ? "text-emerald-700" : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
